import React, { useState, useEffect, useCallback } from 'react';
import { Modal } from './ui/Modal';
import { Button } from './ui/Button';
import { Input, Textarea } from './ui/Input';
import { Select } from './ui/Select';
import { ExpenseEntry, SubItemConfig, Attachment, FormFieldConfig, MainCostCategory, Project } from '../types';
import { SUB_ITEM_FORM_CONFIG, COST_CATEGORIES, UNIT_OPTIONS, formatMonthForInput, formatMonthForDisplay, formatCurrency, API_BASE_URL } from '../constants';
import { AttachmentInput } from './AttachmentInput';
import { useUsers } from '../hooks/useUsers';
import { useProjects } from '../hooks/useProjects';
import { useCompanies } from '../hooks/useCompanies';
import { useExpenses } from '../hooks/useExpenses';
import { TrashIcon } from './icons/TrashIcon';
import { PlusIcon } from './icons/PlusIcon';

interface DynamicEntryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
  subItemId: string;
  obraId: string;
  categoryId?: string;
  existingExpense?: ExpenseEntry | null;
  allExpensesForSeries?: ExpenseEntry[];
  isLocked?: boolean;
  editFocus?: 'all' | 'values' | 'info' | null;
}

interface SeriesInfo {
    baseDescription: string;
    firstMonth: string;
    lastMonth: string;
    totalInstallments: number;
    seriesExpenses: ExpenseEntry[];
}

// Helper functions for month string manipulation
const addMonthsToMonthString = (monthStr: string, monthsToAdd: number): string => {
    const [year, month] = monthStr.split('-').map(Number);
    const date = new Date(Date.UTC(year, month - 1, 15));
    date.setUTCMonth(date.getUTCMonth() + monthsToAdd);
    return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`;
};

const monthsBetween = (startStr: string, endStr: string): number => {
    const [startY, startM] = startStr.split('-').map(Number);
    const [endY, endM] = endStr.split('-').map(Number);
    return (endY - startY) * 12 + (endM - startM);
};

const getBaseDescription = (desc?: string): string => {
    if (!desc) return '';
    const match = desc.match(/^(.*) \(Parcela \d+\/\d+\)$/);
    return match ? match[1].trim() : desc;
};

const cleanDescription = (desc?: string): string => {
    if (!desc) return '';
    let d = desc;
    d = d.replace(/ \(Rateio - \d+(?:\.\d+)?%\)$/, '').trim();
    return d;
};

export const DynamicEntryModal: React.FC<DynamicEntryModalProps> = ({ isOpen, onClose, onSubmit, subItemId, obraId, categoryId, existingExpense, allExpensesForSeries = [], isLocked, editFocus = null }) => {
  const { users } = useUsers();
  const { projects } = useProjects();
  const { companies } = useCompanies();
  const { refreshExpenses } = useExpenses();
  const [formData, setFormData] = useState<Partial<ExpenseEntry>>({});
  const [config, setConfig] = useState<SubItemConfig | null>(null);

  // State for recurring series extension
  const [isEditingSeries, setIsEditingSeries] = useState(false);
  const [seriesInfo, setSeriesInfo] = useState<SeriesInfo | null>(null);
  const [newLastMonth, setNewLastMonth] = useState('');
  const [newUnitPriceForExtension, setNewUnitPriceForExtension] = useState<number | undefined>(undefined);

  // Selective period editing state for series
  const [useSelectivePeriod, setUseSelectivePeriod] = useState(false);
  const [selectiveStartMonth, setSelectiveStartMonth] = useState('');
  const [selectiveEndMonth, setSelectiveEndMonth] = useState('');

  // History logs and restore state
  const [historyLogs, setHistoryLogs] = useState<any[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // State for Rateio
  const [isRateioMode, setIsRateioMode] = useState(false);
  const [rateioTotalValue, setRateioTotalValue] = useState<number>(0);
  const [isRateioParcelado, setIsRateioParcelado] = useState(false);
  const [rateioAdditionalProjects, setRateioAdditionalProjects] = useState<string[]>([]);
  const [rateioMonths, setRateioMonths] = useState<{ month: string; percentages: { [projectId: string]: number }; totalAmount?: number }[]>([
    { month: formatMonthForInput(undefined), percentages: { [obraId]: 50 } }
  ]);

  const isRateioEligible = subItemId.startsWith('cio_') || subItemId.startsWith('cdo_');

  const getProjectLabel = useCallback((project: Project) => {
      const company = companies.find(c => c.id === project.companyId);
      // Remove default city suffix if it exists in the name so we can format it nicely
      let baseName = project.name;
      if (company && baseName.includes(`${company.address.city}/${company.address.state}`)) {
          baseName = baseName.replace(` - ${company.address.city}/${company.address.state}`, '');
      }
      return company ? `${baseName} (Cidade: ${company.address.city} - ${company.address.state})` : project.name;
  }, [companies]);

  useEffect(() => {
    // Reset series and rateio state on open
    setIsEditingSeries(false);
    setSeriesInfo(null);
    setNewUnitPriceForExtension(undefined);
    setIsRateioMode(false);
    setRateioTotalValue(0);
    setIsRateioParcelado(false);
    setRateioAdditionalProjects([]);
    setRateioMonths([{ month: formatMonthForInput(undefined), percentages: { [obraId]: 100 }, totalAmount: 0 }]);
    setUseSelectivePeriod(false);
    setSelectiveStartMonth('');
    setSelectiveEndMonth('');

    let formConfig = SUB_ITEM_FORM_CONFIG[subItemId] ? { ...SUB_ITEM_FORM_CONFIG[subItemId] } : null;
    if (formConfig && (subItemId.startsWith('cio_') || subItemId.startsWith('cdo_'))) {
        const hasInstallment = formConfig.formFields.some(f => f.name === 'isInstallment');
        if (!hasInstallment) {
            const fieldsToAdd: FormFieldConfig[] = [
                { name: 'isInstallment', label: 'Lançamento Parcelado', type: 'boolean', required: false, gridCols: 1 },
                { name: 'installmentMonths', label: 'Número de Parcelas', type: 'number', required: false, gridCols: 2 }
            ];
            const attachmentsIndex = formConfig.formFields.findIndex(f => f.name === 'attachments');
            const updatedFields = [...formConfig.formFields];
            if (attachmentsIndex !== -1) {
                updatedFields.splice(attachmentsIndex, 0, ...fieldsToAdd);
            } else {
                updatedFields.push(...fieldsToAdd);
            }
            formConfig.formFields = updatedFields;
        }
    }
    setConfig(formConfig);

    if (formConfig) {
        const initialData = formConfig.formFields.reduce((acc, field) => {
            (acc as any)[field.name] = '';
            return acc;
        }, {} as Partial<ExpenseEntry>);
        
        if (existingExpense) {
            let initialFormData = { ...initialData, ...existingExpense };
            
            // --- Normal Edit/Add Setup ---
            setFormData(initialFormData);

            // Helper to match siblings
            const getGroupKey = (e: ExpenseEntry) => {
                if (e.seriesId) return e.seriesId;
                const parcelMatch = e.description?.match(/^(.*) \(Parcela \d+\/\d+\)$/);
                if (parcelMatch) {
                    const baseDesc = parcelMatch[1].trim();
                    return `synth_${e.projectId}_${e.subCategoryItemId || 'no-sub'}_${(e.supplier || '').toLowerCase().trim()}_${baseDesc.toLowerCase()}`;
                }
                return null;
            };

            const siblings = existingExpense.rateioId
                ? allExpensesForSeries.filter(e => e.rateioId === existingExpense.rateioId)
                : (getGroupKey(existingExpense)
                    ? allExpensesForSeries.filter(e => getGroupKey(e) === getGroupKey(existingExpense))
                    : [existingExpense]);

            if (siblings.length > 0) {
                if (existingExpense.rateioId) {
                    setIsRateioMode(true);
                }
                
                // Obras adicionais (excluindo a obraId ativa)
                const extraProjects = Array.from(new Set(siblings.map(e => e.projectId).filter(pId => pId !== obraId)));
                setRateioAdditionalProjects(extraProjects);

                // Reconstruir lista de meses únicos organizados por competência
                const uniqueMonths = Array.from(new Set(siblings.map(e => e.month || ''))).filter(Boolean).sort();
                
                const initialMonthsList = uniqueMonths.map(mStr => {
                    const monthEntries = siblings.filter(e => e.month === mStr);
                    const percentages: { [projId: string]: number } = { [obraId]: 0 };
                    extraProjects.forEach(pId => { percentages[pId] = 0; });
                    
                    const monthTotal = monthEntries.reduce((s, e) => s + (e.amount || 0), 0);
                    
                    let sumPercents = 0;
                    monthEntries.forEach(e => {
                        let pct = 0;
                        const match = e.description?.match(/\(Rateio - (\d+(?:\.\d+)?)\s*%\)$/);
                        if (match) {
                            pct = parseFloat(match[1]);
                        } else {
                            // Se ainda não é rateio, 100% fica no projeto original se for ele, senão 0
                            pct = (e.projectId === obraId) ? 100 : 0;
                        }
                        percentages[e.projectId] = parseFloat(pct.toFixed(2));
                        sumPercents += pct;
                    });
                    
                    // Normalização simples se der próximo de 100%
                    if (Math.abs(sumPercents - 100) < 1.0) {
                        const scale = 100 / sumPercents;
                        Object.keys(percentages).forEach(k => {
                            percentages[k] = parseFloat((percentages[k] * scale).toFixed(2));
                        });
                    }
                    
                    return { month: mStr, percentages, totalAmount: monthTotal };
                });

                setRateioMonths(initialMonthsList.length > 0 ? initialMonthsList : [{ month: formatMonthForInput(undefined), percentages: { [obraId]: 100 }, totalAmount: existingExpense.amount || 0 }]);

                // Calcular o valor total original do rateio
                const totalEntriesAmount = siblings.reduce((acc, e) => acc + (e.amount || 0), 0);
                const isParcelado = uniqueMonths.length > 1;
                setIsRateioParcelado(isParcelado);
                
                // Se estiver parcelado, o total rateioTotalValue digitado é a soma de todos os meses, senão é o valor total de entrada
                setRateioTotalValue(totalEntriesAmount);
            } else {
                setRateioTotalValue(existingExpense.amount || 0);
            }
        } else {
            const defaultDate = new Date().toISOString().split('T')[0];
            const defaultMonth = formatMonthForInput(undefined);
            setFormData({ ...initialData, date: defaultDate, month: defaultMonth });
        }
    }
  }, [subItemId, existingExpense, isOpen, allExpensesForSeries, obraId]);

  const loadHistory = useCallback(async () => {
    if (!existingExpense?.id) return;
    setLoadingHistory(true);
    try {
        const res = await fetch(`${API_BASE_URL}/expenses/${existingExpense.id}/history`);
        if (res.ok) {
            const data = await res.json();
            setHistoryLogs(data);
        }
    } catch (err) {
        console.error("Erro ao carregar histórico do lançamento:", err);
    } finally {
        setLoadingHistory(false);
    }
  }, [existingExpense?.id]);

  useEffect(() => {
    if (existingExpense?.id && isOpen) {
        loadHistory();
    } else {
        setHistoryLogs([]);
    }
  }, [existingExpense?.id, isOpen, loadHistory]);

  const handleRestoreVersion = async (logId: number) => {
    if (!window.confirm("Deseja realmente desfazer as alterações desse lançamento e reverter para esse estado histórico anterior?")) return;
    try {
        const userStr = localStorage.getItem('lamCustosUser');
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (userStr) {
            try {
                const parsed = JSON.parse(userStr);
                if (parsed.email) headers['X-User-Email'] = parsed.email;
                if (parsed.name) headers['X-User-Name'] = encodeURIComponent(parsed.name);
            } catch (e) {
                console.error(e);
            }
        }

        const res = await fetch(`${API_BASE_URL}/history/restore/${logId}`, {
            method: 'POST',
            headers
        });
        if (res.ok) {
            alert("Lançamento restaurado com sucesso!");
            await refreshExpenses();
            onClose();
        } else {
            const errorData = await res.json();
            alert(`Falha ao restaurar: ${errorData.message || 'Erro desconhecido'}`);
        }
    } catch (err) {
        console.error("Erro ao reverter lançamento:", err);
        alert("Ocorreu um erro ao restaurar.");
    }
  };

  useEffect(() => {
    const qty = formData.quantity;
    const price = formData.unitPrice;
    if (typeof qty === 'number' && typeof price === 'number' && !isNaN(qty) && !isNaN(price)) {
      const calculatedAmount = qty * price;
      
      if (!isEditingSeries && !isRateioMode) {
        setFormData(prev => ({...prev, amount: calculatedAmount}));
      }
      
      // Sync rateioTotalValue with form's calculated amount if in rateio mode
      if (isRateioMode) {
        setRateioTotalValue(calculatedAmount);
      }
    }
  }, [formData.quantity, formData.unitPrice, isEditingSeries, isRateioMode]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
        const { checked } = e.target as HTMLInputElement;
        setFormData(prev => {
            const newState = { ...prev, [name]: checked };
            if (name === 'isRecurring' && !checked) {
                newState.recurrenceMonths = undefined; 
            }
            if (name === 'isRecurring' && checked) {
                newState.isInstallment = false;
                newState.installmentMonths = undefined;
            }
            if (name === 'isInstallment' && !checked) {
                newState.installmentMonths = undefined;
            }
            if (name === 'isInstallment' && checked) {
                newState.isRecurring = false;
                newState.recurrenceMonths = undefined;
            }
            return newState;
        });
        return;
    }
    setFormData(prev => ({ ...prev, [name]: type === 'number' && value !== '' ? parseFloat(value) : value }));
  };

  const handleAttachmentChange = useCallback((attachments: Attachment[]) => {
      setFormData(prev => ({ ...prev, attachments }));
  }, []);

    const metadataFields = ['description', 'supplier', 'requesterId', 'invoice', 'observation', 'attachments'];
    
    // Helper to get changed fields from form vs existing
    const getChangedMetadata = () => {
        const changes: Partial<ExpenseEntry> = {};
        metadataFields.forEach(field => {
            const formVal = (formData as any)[field];
            const existVal = (existingExpense as any)?.[field];
            
            // Special handling for attachments and descriptions
            if (field === 'attachments') {
                const formAtts = formVal || [];
                const existAtts = existVal || [];
                if (JSON.stringify(formAtts) !== JSON.stringify(existAtts)) {
                    changes.attachments = formAtts;
                }
            } else if (field === 'description') {
                const cleanForm = cleanDescription(getBaseDescription(formVal));
                const cleanExist = cleanDescription(getBaseDescription(existVal));
                if (cleanForm !== cleanExist) {
                    changes.description = cleanForm;
                }
            } else {
                if (formVal !== existVal && formVal !== undefined) {
                    (changes as any)[field] = formVal;
                }
            }
        });
        return changes;
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        // Helper to match siblings
        const getGroupKey = (e: ExpenseEntry) => {
            if (e.rateioId) return e.rateioId;
            if (e.seriesId) return e.seriesId;
            const parcelMatch = e.description?.match(/^(.*) \(Parcela \d+\/\d+\)$/);
            if (parcelMatch) {
                const baseDesc = parcelMatch[1].trim();
                return `synth_${e.projectId}_${e.subCategoryItemId || 'no-sub'}_${(e.supplier || '').toLowerCase().trim()}_${baseDesc.toLowerCase()}`;
            }
            return null;
        };

        const siblings = existingExpense
            ? (existingExpense.rateioId
                ? allExpensesForSeries.filter(e => e.rateioId === existingExpense.rateioId)
                : (existingExpense.seriesId
                    ? allExpensesForSeries.filter(e => e.seriesId === existingExpense.seriesId)
                    : (getGroupKey(existingExpense)
                        ? allExpensesForSeries.filter(e => getGroupKey(e) === getGroupKey(existingExpense))
                        : [existingExpense]
                      )
                  )
              )
            : [];

        // --- Handle Rateio Submission ---
        if (isRateioMode && isRateioEligible) {
            if (rateioAdditionalProjects.length === 0) {
                alert('Por favor, selecione pelo menos uma obra adicional para o rateio.');
                return;
            }
            if (rateioTotalValue <= 0) {
                alert('O valor total do rateio deve ser maior que zero.');
                return;
            }
            if (rateioMonths.length === 0) {
                alert('Adicione pelo menos um mês para o rateio.');
                return;
            }

            // Validate that each month sums to 100%
            for (const rm of rateioMonths) {
                const totalPercent = Object.values(rm.percentages).reduce((sum, p) => sum + (p || 0), 0);
                if (Math.abs(totalPercent - 100) > 0.01) {
                    alert(`A soma das porcentagens para o mês ${formatMonthForDisplay(rm.month)} deve ser 100% (atualmente ${totalPercent}%).`);
                    return;
                }
            }

            const creations: Partial<ExpenseEntry>[] = [];
            const deletions: string[] = [];
            const rateioId = existingExpense?.rateioId || `rateio_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
            
            let mainCategory = categoryId as MainCostCategory;
            if (!mainCategory) {
                for (const cat of COST_CATEGORIES) {
                    if (cat.subItems.some(s => s.id === subItemId)) {
                        mainCategory = cat.id;
                        break;
                    }
                }
            }
            
            if (!mainCategory) {
                mainCategory = subItemId.split('_')[0].toLowerCase() as MainCostCategory;
            }

            const subCategoryItemId = subItemId;
            let subCategoryItemName = subItemId;
            for (const cat of COST_CATEGORIES) {
                const sub = cat.subItems.find(s => s.id === subItemId);
                if (sub) {
                    subCategoryItemName = sub.name;
                    break;
                }
            }

            // If editing, delete old entries
            if (existingExpense) {
                deletions.push(...siblings.map(e => e.id));
            }

            const activeRateioTotalValue = isRateioParcelado && rateioMonths.length > 0 
                ? rateioTotalValue / rateioMonths.length 
                : rateioTotalValue;

            rateioMonths.forEach(rm => {
                const projectIds = [obraId, ...rateioAdditionalProjects];
                const siblingsForMonth = siblings.filter(e => e.month === rm.month);
                const originalSibling = siblingsForMonth.find(e => e.projectId === obraId) 
                    || siblingsForMonth[0];

                projectIds.forEach(pId => {
                    const percent = rm.percentages[pId] || 0;
                    if (percent <= 0) return;

                    const monthBaseAmount = (existingExpense && rm.totalAmount !== undefined)
                        ? rm.totalAmount
                        : activeRateioTotalValue;

                    const amount = monthBaseAmount * (percent / 100);
                    const baseExpenseFields = originalSibling ? { ...originalSibling } : { ...formData };
                    
                    const finalSupplier = formData.supplier !== undefined ? formData.supplier : baseExpenseFields.supplier;
                    const finalRequester = formData.requesterId !== undefined ? formData.requesterId : baseExpenseFields.requesterId;
                    const finalInvoice = formData.invoice !== undefined ? formData.invoice : baseExpenseFields.invoice;
                    const finalObservation = formData.observation !== undefined ? formData.observation : baseExpenseFields.observation;
                    const finalAttachments = formData.attachments !== undefined ? formData.attachments : baseExpenseFields.attachments;
                    
                    const rawDesc = originalSibling ? originalSibling.description : (formData.description || '');
                    const baseDescNoRateio = cleanDescription(getBaseDescription(rawDesc));
                    const finalDescription = `${baseDescNoRateio} (Rateio - ${percent}%)`.trim();

                    creations.push({
                        ...baseExpenseFields,
                        id: undefined,
                        projectId: pId,
                        mainCategory,
                        subCategoryItemId,
                        subCategoryItemName,
                        month: rm.month,
                        date: originalSibling ? originalSibling.date : `${rm.month}-01`,
                        amount,
                        supplier: finalSupplier,
                        requesterId: finalRequester,
                        invoice: finalInvoice,
                        description: finalDescription,
                        observation: finalObservation,
                        attachments: finalAttachments,
                        rateioId,
                        seriesId: originalSibling ? originalSibling.seriesId : undefined,
                    });
                });
            });

            onSubmit({ updates: [], creations, deletions });
            return;
        }

        // --- Handle Normal Submission ---
        
        // Check for metadata changes to sync to siblings if not specifically editing values
        const changedMetadata = getChangedMetadata();
        const hasMetadataChanges = Object.keys(changedMetadata).length > 0;

        if (existingExpense && !isEditingSeries && siblings.length > 1 && hasMetadataChanges && editFocus !== 'values') {
            // Smart sync metadata for group (Series/Parcelado/Synthetic)
            const updates = siblings.map(exp => {
                if (exp.id === existingExpense.id) {
                    // Current entry gets full formData update
                    return { ...exp, ...formData };
                }
                
                // Other siblings get ONLY changed metadata
                const update: any = { ...exp, ...changedMetadata };
                
                // Preserve description suffix if it was specialized (like Parcela)
                if (changedMetadata.description && exp.description) {
                    const suffixMatch = exp.description.match(/ (\(Parcela \d+\/\d+\))$/);
                    if (suffixMatch) {
                        update.description = `${changedMetadata.description} ${suffixMatch[1]}`;
                    }
                }
                
                return update;
            });
            
            onSubmit({ updates, creations: [], deletions: [] });
            return;
        }

        // --- Handle Normal Submission (potentially switching from Rateio) ---
        if (!isRateioMode && existingExpense?.rateioId && allExpensesForSeries) {
            const oldEntries = allExpensesForSeries.filter(e => e.rateioId === existingExpense.rateioId);
            const deletions = oldEntries.map(e => e.id);
            
            // Create a new normal entry from current form data
            const creations = [{
                ...formData,
                id: undefined,
                rateioId: undefined,
                projectId: obraId,
                mainCategory: subItemId.split('_')[0].toLowerCase() as MainCostCategory,
                subCategoryItemId: subItemId,
                subCategoryItemName: (function() {
                    for (const cat of COST_CATEGORIES) {
                        const sub = cat.subItems.find(s => s.id === subItemId);
                        if (sub) return sub.name;
                    }
                    return subItemId;
                })(),
            }];
            
            onSubmit({ updates: [], creations, deletions });
            return;
        }

    // --- Handle Series Extension/Update Submission ---
    if (isEditingSeries && seriesInfo) {
        const isExtending = newLastMonth > seriesInfo.lastMonth;
        
        if (newLastMonth < seriesInfo.lastMonth) {
            alert('A nova data final não pode ser anterior à data final atual da série.');
            return;
        }
        
        const monthsToAdd = isExtending ? monthsBetween(seriesInfo.lastMonth, newLastMonth) : 0;
        const newTotalInstallments = seriesInfo.totalInstallments + monthsToAdd;

        // Get the base description from the form, stripping any existing (Parcela X/Y)
        let baseDescription = formData.description || seriesInfo.baseDescription;
        baseDescription = baseDescription.replace(/ \(Parcela \d+\/\d+\)$/, '').trim();

        // 1. Prepare updates for existing expenses
        const updates = seriesInfo.seriesExpenses.map(exp => {
            const isWithinPeriod = !useSelectivePeriod || (
                (!selectiveStartMonth || (exp.month || '') >= selectiveStartMonth) &&
                (!selectiveEndMonth || (exp.month || '') <= selectiveEndMonth)
            );

            const installmentMatch = exp.description?.match(/ \(Parcela (\d+)\/\d+\)$/);
            const currentInstallmentNumber = installmentMatch ? installmentMatch[1] : '?';
            const updatedDescription = `${baseDescription} (Parcela ${currentInstallmentNumber}/${newTotalInstallments})`;

            if (isWithinPeriod) {
                // If in extension mode, we use formData for ALL fields except protected ones
                const { id, month, date, createdAt, projectId, mainCategory, subCategoryItemId, subCategoryItemName, ...restOfForm } = formData;
                return {
                    ...exp,
                    ...restOfForm,
                    description: updatedDescription,
                };
            } else {
                // Keep exp unmodified except for description suffix alignment and potentially changed metadata
                const { id, month, date, createdAt, projectId, mainCategory, subCategoryItemId, subCategoryItemName, amount, unitPrice, quantity, date: d, ...changesToApply } = formData;
                
                // If not in period, we might still want to apply metadata changes if editFocus is info
                if (editFocus === 'info') {
                     return {
                        ...exp,
                        ...changedMetadata,
                        description: updatedDescription,
                    };
                }

                return {
                    ...exp,
                    description: updatedDescription,
                };
            }
        });

        // 2. Prepare new expenses to be created
        const creations: Partial<ExpenseEntry>[] = [];
        if (isExtending) {
            const templateExpense = updates[0]; // Use updated first expense as template

            for (let i = 1; i <= monthsToAdd; i++) {
                const newMonth = addMonthsToMonthString(seriesInfo.lastMonth, i);
                const newInstallmentNumber = seriesInfo.totalInstallments + i;
                
                // Use newUnitPriceForExtension if provided, otherwise use the one from the form
                const unitPrice = newUnitPriceForExtension !== undefined ? newUnitPriceForExtension : templateExpense.unitPrice;
                const quantity = templateExpense.quantity || 1;
                const amount = (typeof unitPrice === 'number' && typeof quantity === 'number') ? unitPrice * quantity : templateExpense.amount;

                creations.push({
                    ...templateExpense,
                    id: undefined, // Let backend/context generate new ID
                    month: newMonth,
                    date: `${newMonth}-01`, // Set a default date for the new month
                    unitPrice,
                    amount,
                    description: `${baseDescription} (Parcela ${newInstallmentNumber}/${newTotalInstallments})`,
                });
            }
        }
        
        onSubmit({ updates, creations });
        return;
    }

    // --- Handle Normal Submission ---
    if (config) {
        for (const field of config.formFields) {
            if (existingExpense && ['isRecurring', 'recurrenceMonths', 'isInstallment', 'installmentMonths'].includes(field.name)) {
                continue;
            }
            if (field.required && !formData[field.name]) {
                alert(`O campo "${field.label}" é obrigatório.`);
                return;
            }
        }
    }
    const qty = formData.quantity;
    const price = formData.unitPrice;
    let finalData = {...formData};
    if (typeof qty === 'number' && typeof price === 'number') {
        finalData.amount = qty * price;
    }
    onSubmit(finalData);
  };
  
  if (!config) return null;

  const renderChanges = (prev: any, curr: any) => {
    if (!prev || !curr) return null;
    const changes: React.ReactNode[] = [];

    // 1. Amount
    const prevAmount = parseFloat(prev.amount || '0');
    const currAmount = parseFloat(curr.amount || '0');
    if (Math.abs(prevAmount - currAmount) > 0.005) {
      changes.push(
        <p key="amount" className="text-brand-lime mb-0.5">
          Alterado valor de <strong>{formatCurrency(prevAmount)}</strong> para <strong>{formatCurrency(currAmount)}</strong>
        </p>
      );
    }

    // 2. Quantity & Unit Price
    const prevQty = parseFloat(prev.quantity || '0');
    const currQty = parseFloat(curr.quantity || '0');
    if (prevQty !== currQty) {
      changes.push(
        <p key="quantity" className="mb-0.5">
          Alterada quantidade de <em>{prevQty || '0'}</em> para <em>{currQty || '0'}</em>
        </p>
      );
    }

    const prevPrice = parseFloat(prev.unitPrice || '0');
    const currPrice = parseFloat(curr.unitPrice || '0');
    if (Math.abs(prevPrice - currPrice) > 0.005) {
      changes.push(
        <p key="unitPrice" className="mb-0.5">
          Alterado valor unitário de <strong>{formatCurrency(prevPrice)}</strong> para <strong>{formatCurrency(currPrice)}</strong>
        </p>
      );
    }

    // 3. Description
    if (prev.description !== curr.description) {
      changes.push(
        <p key="description" className="mb-0.5">
          Alterada descrição de <em>"{prev.description || 'N/A'}"</em> para <em>"{curr.description || 'N/A'}"</em>
        </p>
      );
    }

    // 4. Supplier
    if (prev.supplier !== curr.supplier) {
      changes.push(
        <p key="supplier" className="mb-0.5">
          Alterado fornecedor de <em>"{prev.supplier || 'N/A'}"</em> para <em>"{curr.supplier || 'N/A'}"</em>
        </p>
      );
    }

    // 5. Invoice
    if (prev.invoice !== curr.invoice) {
      changes.push(
        <p key="invoice" className="mb-0.5">
          Alterada Nota Fiscal de <em>"{prev.invoice || 'N/A'}"</em> para <em>"{curr.invoice || 'N/A'}"</em>
        </p>
      );
    }

    // 6. Date / Paying Date
    const formatDateComp = (d: any) => {
      if (!d) return '';
      try {
        const dStr = typeof d === 'string' ? d : d.toString();
        return dStr.substring(0, 10);
      } catch(e) { return ''; }
    };
    const prevDateFormatted = formatDateComp(prev.date);
    const currDateFormatted = formatDateComp(curr.date);
    if (prevDateFormatted !== currDateFormatted && prevDateFormatted && currDateFormatted) {
      const formatDateStr = (ymd: string) => {
        const parts = ymd.split('-');
        if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
        return ymd;
      };
      changes.push(
        <p key="date" className="mb-0.5">
          Alterada data de pagamento de <em>{formatDateStr(prevDateFormatted)}</em> para <em>{formatDateStr(currDateFormatted)}</em>
        </p>
      );
    }

    // 7. Month (Mês de competência)
    if (prev.month !== curr.month) {
      changes.push(
        <p key="month" className="mb-0.5">
          Alterada competência de <em>{prev.month ? formatMonthForDisplay(prev.month) : 'N/A'}</em> para <em>{curr.month ? formatMonthForDisplay(curr.month) : 'N/A'}</em>
        </p>
      );
    }

    // 8. Requester
    if (prev.requesterId !== curr.requesterId) {
      const getReqName = (rId?: string) => {
        if (!rId) return 'N/A';
        const found = users.find(u => u.id === rId);
        return found ? found.name.toUpperCase() : rId;
      };
      changes.push(
        <p key="requester" className="mb-0.5">
          Alterado requisitante de <em>"{getReqName(prev.requesterId)}"</em> para <em>"{getReqName(curr.requesterId)}"</em>
        </p>
      );
    }

    // 9. Observation
    if (prev.observation !== curr.observation) {
      changes.push(
        <p key="observation" className="mb-0.5">
          Alterada observação de <em>"{prev.observation || 'Nenhum'}"</em> para <em>"{curr.observation || 'Nenhum'}"</em>
        </p>
      );
    }

    // 10. Contract / Contact / License Plate / Unit
    if (prev.contract !== curr.contract) {
      changes.push(<p key="contract" className="mb-0.5">Alterado contrato de <em>"{prev.contract || 'N/A'}"</em> para <em>"{curr.contract || 'N/A'}"</em></p>);
    }
    if (prev.contact !== curr.contact) {
      changes.push(<p key="contact" className="mb-0.5">Alterado contato de <em>"{prev.contact || 'N/A'}"</em> para <em>"{curr.contact || 'N/A'}"</em></p>);
    }
    if (prev.licensePlate !== curr.licensePlate) {
      changes.push(<p key="licensePlate" className="mb-0.5">Alterada placa de <em>"{prev.licensePlate || 'N/A'}"</em> para <em>"{curr.licensePlate || 'N/A'}"</em></p>);
    }
    if (prev.unit !== curr.unit) {
      changes.push(<p key="unit" className="mb-0.5">Alterada unidade de <em>"{prev.unit || 'N/A'}"</em> para <em>"{curr.unit || 'N/A'}"</em></p>);
    }

    // 11. Attachments count
    const prevAtts = Array.isArray(prev.attachments) ? prev.attachments : [];
    const currAtts = Array.isArray(curr.attachments) ? curr.attachments : [];
    if (prevAtts.length !== currAtts.length) {
      changes.push(
        <p key="attachments" className="mb-0.5">
          Anexos alterados: de <strong>{prevAtts.length}</strong> para <strong>{currAtts.length}</strong> anexo(s)
        </p>
      );
    }

    if (changes.length === 0) {
      return <p className="text-[10px] text-dark-text-secondary italic">Dados auxiliares atualizados.</p>;
    }

    return <div className="text-[10px] space-y-0.5 text-dark-text-secondary mt-1 border-t border-dark-tertiary/20 pt-1">{changes}</div>;
  };

  const employeeOptions = [...users].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')).map(e => ({ value: e.id, label: e.name.toUpperCase() }));

  const title = isEditingSeries ? 'Editar Série de Lançamentos Recorrentes' : `${existingExpense ? 'Editar' : 'Adicionar'} Lançamento`;

  // --- RENDER LOGIC ---

  const renderField = (field: FormFieldConfig) => {
    const value = formData[field.name] as any;
    const gridSpan = field.gridCols ? `lg:col-span-${field.gridCols}` : 'lg:col-span-1';
    const finalGridSpan = field.gridCols === 3 ? 'md:col-span-2 lg:col-span-3' : gridSpan;

    switch (field.type) {
      case 'boolean': return (<div key={field.name} className={`flex items-center pt-5 ${finalGridSpan}`}><input id={field.name} name={field.name} type="checkbox" checked={!!value} onChange={handleChange} className="h-4 w-4 rounded border-dark-tertiary bg-dark-secondary text-brand-teal focus:ring-brand-teal" /><label htmlFor={field.name} className="ml-2 block text-sm font-medium text-dark-text-secondary">{field.label}</label></div>);
      case 'select_requester': return <Select key={field.name} label={field.label} name={field.name} value={value || ''} onChange={handleChange} options={employeeOptions} placeholderOption="Selecione um requisitante" required={field.required} containerClassName={finalGridSpan} />;
      case 'select_unit': return <Select key={field.name} label={field.label} name={field.name} value={value || ''} onChange={handleChange} options={UNIT_OPTIONS} placeholderOption="Selecione uma unidade" required={field.required} containerClassName={finalGridSpan} />;
      case 'textarea': return <Textarea key={field.name} label={field.label} name={field.name} value={value || ''} onChange={handleChange} required={field.required} containerClassName={`md:col-span-2 ${finalGridSpan}`} />;
      case 'attachments': return <AttachmentInput key={field.name} label={field.label} attachments={value || []} onChange={handleAttachmentChange} containerClassName={`md:col-span-2 ${finalGridSpan}`} />;
      case 'month': return <Input key={field.name} label={field.label} name={field.name} type="month" value={formatMonthForInput(value) || ''} onChange={handleChange} required={field.required} containerClassName={finalGridSpan} />;
      case 'amount': return <Input key={field.name} label={field.label} name={field.name} type="number" step="0.01" value={value || ''} onChange={handleChange} required={field.required} containerClassName={finalGridSpan} />;
      default: return <Input key={field.name} label={field.label} name={field.name} type={field.type} value={value || ''} onChange={handleChange} required={field.required} step={field.type === 'number' ? 'any' : undefined} containerClassName={finalGridSpan}/>;
    }
  };

  const isValueField = (fieldName: string) => {
    return ['unitPrice', 'quantity', 'amount', 'isDone', 'amountPaid', 'month', 'date'].includes(fieldName);
  };

  const renderFormFields = () => {
    let filteredFields = config.formFields;
    if (editFocus === 'values') {
        filteredFields = filteredFields.filter(f => isValueField(f.name));
    } else if (editFocus === 'info') {
        filteredFields = filteredFields.filter(f => !isValueField(f.name));
    }
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredFields.map(field => {
                if (existingExpense && ['isRecurring', 'recurrenceMonths', 'isInstallment', 'installmentMonths'].includes(field.name)) return null;
                if (field.name === 'recurrenceMonths' && !formData.isRecurring) return null;
                if (field.name === 'installmentMonths' && !formData.isInstallment) return null;
                return renderField(field);
            })}
        </div>
    );
  };

  const renderTotalAmount = () => {
    if (editFocus === 'info') return null;
    return (
        formData.quantity !== undefined && formData.unitPrice !== undefined && (
            <div className="p-3 bg-dark-surface rounded-md text-right mt-4">
                <span className="text-sm text-dark-text-secondary">Valor Total: </span>
                <span className="text-lg font-bold text-brand-lime">{formatCurrency(formData.amount || 0)}</span>
            </div>
        )
    );
  };

  const renderEditFocusBanner = () => {
    if (!editFocus) return null;
    if (editFocus === 'values') {
        return (
            <div className="mb-4 p-3 bg-teal-950/20 border border-teal-800 rounded-md text-xs text-teal-300 flex items-start gap-2">
                <span className="material-symbols-outlined text-teal-400">info</span>
                <div>
                    <span className="font-bold">Modo de Edição de Valores:</span> Os campos de informações gerais (fornecedor, descrição, etc.) foram ocultados para que você altere apenas o valor deste lançamento.
                </div>
            </div>
        );
    }
    if (editFocus === 'info') {
        return (
            <div className="mb-4 p-3 bg-blue-950/20 border border-blue-800 rounded-md text-xs text-blue-300 flex items-start gap-2">
                <span className="material-symbols-outlined text-blue-400">info</span>
                <div>
                    <span className="font-bold">Modo de Edição de Informações Gerais:</span> Os campos de valores (preço, quantidade, etc.) foram ocultados para que você altere apenas os dados cadastrais. {isEditingSeries && "Suas alterações serão aplicadas a todas os lançamentos desta série."}
                </div>
            </div>
        );
    }
    return null;
  };

  const handleAddRateioMonth = () => {
    const lastMonth = rateioMonths[rateioMonths.length - 1]?.month || formatMonthForInput(undefined);
    const nextMonth = addMonthsToMonthString(lastMonth, 1);
    
    // Default to 100% for current project, 0% for others for the new month
    const percentages: { [projectId: string]: number } = { [obraId]: 100 };
    rateioAdditionalProjects.forEach(pId => {
        percentages[pId] = 0;
    });
    
    setRateioMonths([...rateioMonths, { month: nextMonth, percentages }]);
  };

  const handleRemoveRateioMonth = (index: number) => {
    setRateioMonths(rateioMonths.filter((_, i) => i !== index));
  };

  const handleRateioMonthChange = (index: number, field: string, value: any) => {
    const newMonths = [...rateioMonths];
    (newMonths[index] as any)[field] = value;
    setRateioMonths(newMonths);
  };

  const handleRateioPercentageChange = (monthIndex: number, projectId: string, value: number) => {
    const newMonths = [...rateioMonths];
    newMonths[monthIndex].percentages = {
        ...newMonths[monthIndex].percentages,
        [projectId]: value
    };
    setRateioMonths(newMonths);
  };

  const handleAddRateioProject = () => {
    setRateioAdditionalProjects([...rateioAdditionalProjects, '']);
  };

  const handleRemoveRateioProject = (index: number) => {
    const projectToRemove = rateioAdditionalProjects[index];
    const newAdditional = rateioAdditionalProjects.filter((_, i) => i !== index);
    setRateioAdditionalProjects(newAdditional);
    
    // Remove from all months
    setRateioMonths(rateioMonths.map(rm => {
        const newPercentages = { ...rm.percentages };
        delete newPercentages[projectToRemove];
        return { ...rm, percentages: newPercentages };
    }));
  };

  const handleRateioProjectChange = (index: number, newProjectId: string) => {
    const oldProjectId = rateioAdditionalProjects[index];
    const newAdditional = [...rateioAdditionalProjects];
    newAdditional[index] = newProjectId;
    setRateioAdditionalProjects(newAdditional);
    
    // Update in all months
    setRateioMonths(rateioMonths.map(rm => {
        const newPercentages = { ...rm.percentages };
        if (oldProjectId) {
            const oldVal = newPercentages[oldProjectId];
            delete newPercentages[oldProjectId];
            newPercentages[newProjectId] = oldVal || 0;
        } else {
            newPercentages[newProjectId] = 0;
        }
        return { ...rm, percentages: newPercentages };
    }));
  };

  const activeRateioTotalValueUI = isRateioParcelado && rateioMonths.length > 0
      ? rateioTotalValue / rateioMonths.length
      : rateioTotalValue;

  if (isEditingSeries && seriesInfo) {
      return (
          <Modal isOpen={isOpen} onClose={onClose} title={title} size="2xl">
              <form onSubmit={handleSubmit} className="space-y-6">
                  <fieldset disabled={isLocked}>
                      {isRateioEligible && (
                          <div className="flex bg-dark-secondary p-1 rounded-lg mb-6">
                              <button
                                  type="button"
                                  onClick={() => setIsRateioMode(false)}
                                  className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${!isRateioMode ? 'bg-brand-teal text-white shadow-sm' : 'text-dark-text-secondary hover:text-dark-text-primary'}`}
                              >
                                  Lançamento Normal
                              </button>
                              <button
                                  type="button"
                                  onClick={() => setIsRateioMode(true)}
                                  className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${isRateioMode ? 'bg-brand-teal text-white shadow-sm' : 'text-dark-text-secondary hover:text-dark-text-primary'}`}
                              >
                                  Lançamento de Rateio
                              </button>
                          </div>
                      )}

                      {isRateioMode ? (
                          <div className="space-y-6">
                              {existingExpense && (
                                  <div className="p-3 bg-amber-950/25 border border-amber-800/60 rounded-md text-xs text-amber-300 flex items-start gap-2">
                                      <span className="material-symbols-outlined text-amber-400 text-sm">info</span>
                                      <div>
                                          <span className="font-bold">Lançamento Existente:</span> A estrutura de parcelas/competências e valores do lançamento original foi preservada. Selecione as obras do rateio e defina as porcentagens para cada obra.
                                      </div>
                                  </div>
                              )}

                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <div className="space-y-4">
                                      <Input 
                                          label="Valor Total do Rateio"
                                          type="number"
                                          step="0.01"
                                          value={rateioTotalValue || ''}
                                          onChange={(e) => setRateioTotalValue(parseFloat(e.target.value))}
                                          disabled={!!existingExpense}
                                          required
                                      />
                                      <div className="flex items-center">
                                          <input 
                                              type="checkbox" 
                                              id="isRateioParcelado1" 
                                              checked={isRateioParcelado} 
                                              onChange={(e) => setIsRateioParcelado(e.target.checked)} 
                                              disabled={!!existingExpense}
                                              className="mr-2 h-4 w-4 rounded border-dark-tertiary bg-dark-secondary text-brand-teal focus:ring-brand-teal disabled:opacity-50"
                                          />
                                          <label htmlFor="isRateioParcelado1" className="text-sm font-medium text-dark-text-secondary">
                                              Parcelar valor total entre os meses
                                          </label>
                                      </div>
                                  </div>
                                  <div className="space-y-3">
                                      <div className="flex justify-between items-center">
                                          <label className="block text-xs font-medium text-dark-text-secondary uppercase tracking-wider">Obras Adicionais para Rateio</label>
                                          <Button type="button" variant="ghost" size="sm" onClick={handleAddRateioProject} className="text-brand-teal h-auto py-0">
                                              <PlusIcon className="h-4 w-4 mr-1" /> Adicionar Obra
                                          </Button>
                                      </div>
                                      <div className="space-y-2">
                                          {rateioAdditionalProjects.map((pId, pIdx) => (
                                              <div key={pIdx} className="flex items-center space-x-2">
                                                  <div className="flex-1">
                                                      <Select 
                                                          value={pId}
                                                          onChange={(e) => handleRateioProjectChange(pIdx, e.target.value)}
                                                          options={projects.filter(p => p.id !== obraId && (!rateioAdditionalProjects.includes(p.id) || p.id === pId)).map(p => ({ value: p.id, label: getProjectLabel(p) }))}
                                                          placeholderOption="Selecione a obra"
                                                          required
                                                      />
                                                  </div>
                                                  <button type="button" onClick={() => handleRemoveRateioProject(pIdx)} className="text-red-400 hover:text-red-300 p-1">
                                                      <TrashIcon className="h-4 w-4" />
                                                  </button>
                                              </div>
                                          ))}
                                          {rateioAdditionalProjects.length === 0 && (
                                              <p className="text-xs text-dark-text-secondary italic">Nenhuma obra adicional selecionada.</p>
                                          )}
                                      </div>
                                  </div>
                              </div>

                              <div className="space-y-4">
                                  <div className="flex justify-between items-center">
                                      <h4 className="text-sm font-semibold text-dark-text-primary uppercase tracking-wider">Distribuição por Mês</h4>
                                      {!existingExpense && (
                                          <Button type="button" variant="ghost" size="sm" onClick={handleAddRateioMonth} className="text-brand-teal">
                                              + Adicionar Mês
                                          </Button>
                                      )}
                                  </div>
                                  
                                  {rateioMonths.map((rm, index) => {
                                      const totalPercent = Object.values(rm.percentages).reduce((sum, p) => sum + p, 0);
                                      const isTotalValid = Math.abs(totalPercent - 100) < 0.01;
                                      const monthBaseAmountUI = rm.totalAmount !== undefined ? rm.totalAmount : activeRateioTotalValueUI;

                                      return (
                                          <div key={index} className={`p-4 bg-dark-surface rounded-lg border ${isTotalValid ? 'border-dark-tertiary' : 'border-red-500/50'} space-y-4`}>
                                              <div className="flex justify-between items-start">
                                                  <div className="w-1/3">
                                                      <Input 
                                                          label="Mês"
                                                          type="month"
                                                          value={rm.month}
                                                          onChange={(e) => handleRateioMonthChange(index, 'month', e.target.value)}
                                                          disabled={!!existingExpense}
                                                          required
                                                      />
                                                  </div>
                                                  <div className="flex-1 ml-4 space-y-3">
                                                      <div className="grid grid-cols-2 gap-3">
                                                          <div>
                                                              <label className="block text-[10px] font-medium text-dark-text-secondary mb-1 truncate">
                                                                  {projects.find(p => p.id === obraId)?.name || 'Obra Atual'} (%)
                                                              </label>
                                                              <Input 
                                                                  type="number"
                                                                  value={rm.percentages[obraId] || 0}
                                                                  onChange={(e) => handleRateioPercentageChange(index, obraId, parseFloat(e.target.value))}
                                                                  min="0"
                                                                  max="100"
                                                                  required
                                                              />
                                                              <p className="text-[10px] text-brand-lime mt-1">
                                                                  {formatCurrency(monthBaseAmountUI * ((rm.percentages[obraId] || 0) / 100))}
                                                              </p>
                                                          </div>
                                                          {rateioAdditionalProjects.map(pId => (
                                                              <div key={pId}>
                                                                  <label className="block text-[10px] font-medium text-dark-text-secondary mb-1 truncate">
                                                                      {projects.find(p => p.id === pId)?.name || 'Obra Rateio'} (%)
                                                                  </label>
                                                                  <Input 
                                                                      type="number"
                                                                      value={rm.percentages[pId] || 0}
                                                                      onChange={(e) => handleRateioPercentageChange(index, pId, parseFloat(e.target.value))}
                                                                      min="0"
                                                                      max="100"
                                                                      required
                                                                  />
                                                                  <p className="text-[10px] text-brand-lime mt-1">
                                                                      {formatCurrency(monthBaseAmountUI * ((rm.percentages[pId] || 0) / 100))}
                                                                  </p>
                                                              </div>
                                                          ))}
                                                      </div>
                                                      <div className="flex justify-between items-center pt-2 border-t border-dark-tertiary">
                                                          <span className={`text-[10px] font-bold ${isTotalValid ? 'text-brand-lime' : 'text-red-400'}`}>
                                                              Total: {totalPercent.toFixed(1)}%
                                                          </span>
                                                          {!isTotalValid && (
                                                              <span className="text-[10px] text-red-400 italic">A soma deve ser 100%</span>
                                                          )}
                                                      </div>
                                                  </div>
                                                  {!existingExpense && (
                                                      <button type="button" onClick={() => handleRemoveRateioMonth(index)} className="text-red-400 hover:text-red-300 p-1 ml-2">
                                                          <TrashIcon className="h-5 w-5" />
                                                      </button>
                                                  )}
                                              </div>
                                          </div>
                                      );
                                  })}
                              </div>
                          </div>
                      ) : (
                          <>
                              <div className='mb-6 p-4 bg-dark-surface rounded-lg border border-dark-tertiary space-y-4'>
                                  <div className="flex flex-col space-y-1">
                                      <p className='text-sm text-dark-text-secondary'>
                                        <strong className='text-dark-text-primary'>Informações da Série:</strong> {seriesInfo.totalInstallments} parcelas, de {formatMonthForDisplay(seriesInfo.firstMonth)} a {formatMonthForDisplay(seriesInfo.lastMonth)}.
                                      </p>
                                      <p className='text-xs text-gray-500 dark:text-dark-text-secondary'>
                                        Nota: Por padrão, alterações nos campos abaixo serão aplicadas a todas as parcelas.
                                      </p>
                                  </div>

                                  <div className="pt-3 border-t border-dark-tertiary space-y-3">
                                      <div className="flex items-center">
                                          <input 
                                              type="checkbox" 
                                              id="useSelectivePeriod" 
                                              checked={useSelectivePeriod} 
                                              onChange={(e) => setUseSelectivePeriod(e.target.checked)} 
                                              className="mr-2 h-4 w-4 rounded border-dark-tertiary bg-dark-secondary text-brand-teal focus:ring-brand-teal"
                                          />
                                          <label htmlFor="useSelectivePeriod" className="text-sm font-semibold text-dark-text-secondary cursor-pointer">
                                              Selecionar período para alteração
                                          </label>
                                      </div>

                                      {useSelectivePeriod && (
                                          <div className="grid grid-cols-2 gap-4 p-3 bg-dark-secondary rounded-md border border-dark-tertiary">
                                              <div>
                                                  <label className="block text-xs font-medium text-dark-text-secondary mb-1">Mês de Início</label>
                                                  <input 
                                                      type="month"
                                                      value={selectiveStartMonth}
                                                      onChange={(e) => setSelectiveStartMonth(e.target.value)}
                                                      min={seriesInfo.firstMonth}
                                                      max={seriesInfo.lastMonth}
                                                      className="block w-full rounded-md border-0 bg-dark-surface py-1.5 text-dark-text-primary shadow-sm ring-1 ring-inset ring-dark-tertiary focus:ring-2 focus:ring-inset focus:ring-brand-teal sm:text-sm sm:leading-6"
                                                  />
                                              </div>
                                              <div>
                                                  <label className="block text-xs font-medium text-dark-text-secondary mb-1">Mês de Fim</label>
                                                  <input 
                                                      type="month"
                                                      value={selectiveEndMonth}
                                                      onChange={(e) => setSelectiveEndMonth(e.target.value)}
                                                      min={seriesInfo.firstMonth}
                                                      max={seriesInfo.lastMonth}
                                                      className="block w-full rounded-md border-0 bg-dark-surface py-1.5 text-dark-text-primary shadow-sm ring-1 ring-inset ring-dark-tertiary focus:ring-2 focus:ring-inset focus:ring-brand-teal sm:text-sm sm:leading-6"
                                                  />
                                              </div>
                                          </div>
                                      )}
                                  </div>
                              </div>

                              {renderEditFocusBanner()}
                              {renderFormFields()}
                              {renderTotalAmount()}

                              <div className="mt-8 pt-6 border-t border-dark-tertiary">
                                  <h4 className="text-sm font-semibold text-dark-text-primary mb-4 uppercase tracking-wider">Extensão da Série (Opcional)</h4>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 border border-dashed border-dark-tertiary rounded-lg">
                                      <Input 
                                          label="Estender recorrência até o mês de:"
                                          type="month"
                                          value={newLastMonth}
                                          onChange={(e) => setNewLastMonth(e.target.value)}
                                          min={seriesInfo.lastMonth}
                                          containerClassName='mb-0'
                                      />
                                      <Input 
                                          label="Novo valor unitário para extensão:"
                                          type="number"
                                          step="0.01"
                                          value={newUnitPriceForExtension || ''}
                                          onChange={(e) => setNewUnitPriceForExtension(e.target.value !== '' ? parseFloat(e.target.value) : undefined)}
                                          placeholder="Opcional (mantém o atual se vazio)"
                                          containerClassName='mb-0'
                                      />
                                  </div>
                                  {newUnitPriceForExtension !== undefined && formData.quantity !== undefined && (
                                      <div className="mt-2 text-right text-xs text-brand-lime font-medium">
                                        Novo valor total por parcela estendida: {formatCurrency(newUnitPriceForExtension * (formData.quantity || 1))}
                                      </div>
                                  )}
                              </div>


                          </>
                      )}
                  </fieldset>

                  <div className="flex justify-end space-x-3 pt-4 border-t border-dark-tertiary mt-6">
                      <Button type="button" variant="secondary" onClick={onClose}>Cancelar</Button>
                      <Button type="submit" variant="primary" disabled={isLocked}>
                        {isLocked ? 'Obra Bloqueada' : 'Salvar Alterações na Série'}
                      </Button>
                  </div>
              </form>
          </Modal>
      );
  }
  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="2xl">
      <form onSubmit={handleSubmit} className="space-y-4">
        <fieldset disabled={isLocked}>
            {isRateioEligible && (
                <div className="flex bg-dark-secondary p-1 rounded-lg mb-6">
                    <button
                        type="button"
                        onClick={() => setIsRateioMode(false)}
                        className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${!isRateioMode ? 'bg-brand-teal text-white shadow-sm' : 'text-dark-text-secondary hover:text-dark-text-primary'}`}
                    >
                        Lançamento Normal
                    </button>
                    <button
                        type="button"
                        onClick={() => setIsRateioMode(true)}
                        className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${isRateioMode ? 'bg-brand-teal text-white shadow-sm' : 'text-dark-text-secondary hover:text-dark-text-primary'}`}
                    >
                        Lançamento de Rateio
                    </button>
                </div>
            )}

            {isRateioMode ? (
                <div className="space-y-6">
                    {existingExpense && (
                        <div className="p-3 bg-amber-950/25 border border-amber-800/60 rounded-md text-xs text-amber-300 flex items-start gap-2">
                            <span className="material-symbols-outlined text-amber-400 text-sm">info</span>
                            <div>
                                <span className="font-bold">Lançamento Existente:</span> A estrutura de parcelas/competências e valores do lançamento original foi preservada. Selecione as obras do rateio e defina as porcentagens para cada obra.
                            </div>
                        </div>
                    )}

                    <div className="space-y-4">
                        <Input 
                            label="Valor Total do Rateio"
                            type="number"
                            step="0.01"
                            value={rateioTotalValue || ''}
                            onChange={(e) => setRateioTotalValue(parseFloat(e.target.value))}
                            disabled={!!existingExpense}
                            required
                        />
                        <div className="flex items-center">
                            <input 
                                type="checkbox" 
                                id="isRateioParcelado2" 
                                checked={isRateioParcelado} 
                                onChange={(e) => setIsRateioParcelado(e.target.checked)} 
                                disabled={!!existingExpense}
                                className="mr-2 h-4 w-4 rounded border-dark-tertiary bg-dark-secondary text-brand-teal focus:ring-brand-teal disabled:opacity-50"
                            />
                            <label htmlFor="isRateioParcelado2" className="text-sm font-medium text-dark-text-secondary">
                                Parcelar valor total entre os meses
                            </label>
                        </div>
                        
                        <div className="space-y-3">
                            <div className="flex justify-between items-center">
                                <label className="block text-sm font-medium text-dark-text-primary">Obras Adicionais para Rateio</label>
                                <Button type="button" variant="ghost" size="sm" onClick={handleAddRateioProject} className="text-brand-teal">
                                    <PlusIcon className="h-4 w-4 mr-1" /> Adicionar Obra
                                </Button>
                            </div>
                            
                            {rateioAdditionalProjects.map((pId, idx) => (
                                <div key={idx} className="flex items-center space-x-2">
                                    <div className="flex-1">
                                        <Select 
                                            value={pId}
                                            onChange={(e) => handleRateioProjectChange(idx, e.target.value)}
                                            options={projects.filter(p => p.id !== obraId && (!rateioAdditionalProjects.includes(p.id) || p.id === pId)).map(p => ({ value: p.id, label: getProjectLabel(p) }))}
                                            placeholderOption="Selecione a obra"
                                            required
                                            containerClassName="mb-0"
                                        />
                                    </div>
                                    <button type="button" onClick={() => handleRemoveRateioProject(idx)} className="text-red-400 hover:text-red-300 p-2">
                                        <TrashIcon className="h-5 w-5" />
                                    </button>
                                </div>
                            ))}
                            {rateioAdditionalProjects.length === 0 && (
                                <p className="text-xs text-dark-text-secondary italic">Nenhuma obra adicional selecionada.</p>
                            )}
                        </div>
                    </div>

                    <div className="space-y-4">
                        <div className="flex justify-between items-center">
                            <h4 className="text-sm font-semibold text-dark-text-primary uppercase tracking-wider">Distribuição por Mês</h4>
                            {!existingExpense && (
                                <Button type="button" variant="ghost" size="sm" onClick={handleAddRateioMonth} className="text-brand-teal">
                                    + Adicionar Mês
                                </Button>
                            )}
                        </div>
                        
                        {rateioMonths.map((rm, index) => {
                            const monthBaseAmountUI = rm.totalAmount !== undefined ? rm.totalAmount : activeRateioTotalValueUI;
                            return (
                                <div key={index} className="p-4 bg-dark-surface rounded-lg border border-dark-tertiary space-y-4">
                                    <div className="flex justify-between items-start">
                                        <div className="w-1/3">
                                            <Input 
                                                label="Mês"
                                                type="month"
                                                value={rm.month}
                                                onChange={(e) => handleRateioMonthChange(index, 'month', e.target.value)}
                                                containerClassName="mb-0"
                                                disabled={!!existingExpense}
                                                required
                                            />
                                        </div>
                                        {!existingExpense && (
                                            <button type="button" onClick={() => handleRemoveRateioMonth(index)} className="text-red-400 hover:text-red-300 p-1">
                                                <TrashIcon className="h-5 w-5" />
                                            </button>
                                        )}
                                    </div>
                                    
                                    <div className="space-y-3">
                                        <div className="grid grid-cols-2 gap-4 items-end">
                                            <div>
                                                <label className="block text-xs font-medium text-dark-text-secondary mb-1">Obra Atual (%)</label>
                                                <Input 
                                                    type="number"
                                                    value={rm.percentages[obraId] || 0}
                                                    onChange={(e) => handleRateioPercentageChange(index, obraId, parseFloat(e.target.value))}
                                                    min="0"
                                                    max="100"
                                                    required
                                                    containerClassName="mb-0"
                                                />
                                            </div>
                                            <div className="text-right text-xs text-dark-text-secondary font-medium pb-2">
                                                Valor: <span className="text-brand-lime">{formatCurrency(monthBaseAmountUI * ((rm.percentages[obraId] || 0) / 100))}</span>
                                            </div>
                                        </div>
                                        
                                        {rateioAdditionalProjects.map(pId => {
                                            const project = projects.find(p => p.id === pId);
                                            if (!project) return null;
                                            return (
                                                <div key={pId} className="grid grid-cols-2 gap-4 items-end">
                                                    <div>
                                                        <label className="block text-xs font-medium text-dark-text-secondary mb-1">{project.name} (%)</label>
                                                        <Input 
                                                            type="number"
                                                            value={rm.percentages[pId] || 0}
                                                            onChange={(e) => handleRateioPercentageChange(index, pId, parseFloat(e.target.value))}
                                                            min="0"
                                                            max="100"
                                                            required
                                                            containerClassName="mb-0"
                                                        />
                                                    </div>
                                                    <div className="text-right text-xs text-dark-text-secondary font-medium pb-2">
                                                        Valor: <span className="text-brand-lime">{formatCurrency(monthBaseAmountUI * ((rm.percentages[pId] || 0) / 100))}</span>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                        
                                        <div className="pt-2 border-t border-dark-tertiary flex justify-between items-center">
                                            <span className="text-xs font-semibold text-dark-text-primary">Total:</span>
                                            <span className={`text-xs font-bold ${Math.abs(Object.values(rm.percentages).reduce((s, p) => s + (p || 0), 0) - 100) < 0.01 ? 'text-brand-lime' : 'text-red-400'}`}>
                                                {Object.values(rm.percentages).reduce((s, p) => s + (p || 0), 0)}%
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {!existingExpense && (
                        <div className="pt-4 border-t border-dark-tertiary">
                            <h4 className="text-sm font-semibold text-dark-text-primary mb-4 uppercase tracking-wider">Dados Gerais do Lançamento</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {config.formFields.filter(f => !['amount', 'month', 'date', 'isRecurring', 'recurrenceMonths', 'isInstallment', 'installmentMonths'].includes(f.name as string)).map(field => renderField(field))}
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                <>
                    {renderEditFocusBanner()}
                    {renderFormFields()}
                    {renderTotalAmount()}
                </>
            )}


        </fieldset>

        <div className="flex justify-end space-x-3 pt-4 border-t border-dark-tertiary mt-6">
          <Button type="button" variant="secondary" onClick={onClose}>Cancelar</Button>
          <Button type="submit" variant="primary" disabled={isLocked}>
            {isLocked ? 'Obra Bloqueada' : (existingExpense ? 'Salvar Alterações' : 'Adicionar Lançamento')}
          </Button>
        </div>
      </form>
    </Modal>
  );
};